import { io, Socket } from 'socket.io-client';
import { Song, Player, CorrectGuessSummary } from '../types';

export type SelectSongAck =
  | { ok: true }
  | { ok: false; error: string; code?: string; details?: string };

export type SongDownloadStatusPayload = {
  status: 'starting' | 'success' | 'failed';
  username: string;
  videoId: string;
  pendingCount: number;
  error?: string;
};

export type CurrentRoundInfo = {
  round: number;
  totalRounds: number;
  songUrl: string;
  songTitle: string;
  roundStartTime: number;
  songSelector: { id: string; username: string } | null;
  correctGuesses: CorrectGuessSummary[];
  themeVote: { currentVotes: number; requiredVotes: number; passed: boolean };
};

export type RoomStatePayload = {
  roomId: string;
  players: Player[];
  gameState: string;
  theme?: string;
  currentRound?: number;
  totalRounds?: number;
  roundStartTime?: number;
  pendingSongDownloadsCount?: number;
  scores?: Player[];
  preloadProgress?: { preloaded: number; total: number };
  currentRoundInfo?: CurrentRoundInfo;
  /** The receiving player's stable id. Adopt it: the server may hand back an existing seat. */
  selfId?: string;
  selfSelectedSong?: Song | null;
  selfHasGuessedCorrectly?: boolean;
  selfHasVotedInvalidTheme?: boolean;
};

export type PlayersUpdatePayload = {
  playerId: string;
  username: string;
  players: Player[];
  gameState?: string;
  scores?: Player[];
  reconnected?: boolean;
};

/** Every `onX` helper returns an unsubscribe function so components can clean up precisely. */
export type Unsubscribe = () => void;

export const normalizeRoomId = (roomId: string): string => roomId.trim().toLowerCase();

export const getInvitePath = (roomId: string): string => `/join/${encodeURIComponent(normalizeRoomId(roomId))}`;

export const getInviteUrl = (roomId: string): string => `${window.location.origin}${getInvitePath(roomId)}`;

// Determine the socket server URL
const getSocketUrl = () => {
  // Check if we're running locally
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    // Use the backend server URL with the port from process.env.PORT or the default 54048
    return 'http://localhost:54048';
  }
  // In production, use the current window location origin
  return window.location.origin;
};

// Session storage keys (per browser tab)
const SESSION_ROOM_ID = 'songle_room_id';
const SESSION_USERNAME = 'songle_username';
const SESSION_PLAYER_ID = 'songle_player_id';

// Create a single socket instance
const socket: Socket = io(getSocketUrl(), {
  transports: ['websocket', 'polling'],
  autoConnect: false,
  reconnection: true,
  // Keep trying for as long as the tab is open; the UI shows a "reconnecting" banner meanwhile.
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 10000,
});

const generatePlayerId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
};

/**
 * Stable identity for this tab. It is what lets the server recognise us again after a refresh,
 * a back/forward navigation or a dropped connection, independent of the socket id.
 */
export const getPlayerId = (): string => {
  let playerId = sessionStorage.getItem(SESSION_PLAYER_ID);
  if (!playerId) {
    playerId = generatePlayerId();
    sessionStorage.setItem(SESSION_PLAYER_ID, playerId);
  }
  return playerId;
};

/** The server is authoritative about which seat we hold; remember the id it gave us. */
export const adoptPlayerId = (playerId: string): void => {
  if (playerId) {
    sessionStorage.setItem(SESSION_PLAYER_ID, playerId);
  }
};

// Store session info for reconnection
export const storeSessionInfo = (roomId: string, username: string): void => {
  sessionStorage.setItem(SESSION_ROOM_ID, normalizeRoomId(roomId));
  sessionStorage.setItem(SESSION_USERNAME, username);
};

// Clear session info (keeps the player id so a later join reuses the same identity)
export const clearSessionInfo = (): void => {
  sessionStorage.removeItem(SESSION_ROOM_ID);
  sessionStorage.removeItem(SESSION_USERNAME);
};

// Get stored session info
export const getSessionInfo = (): { roomId: string | null, username: string | null } => {
  const storedRoomId = sessionStorage.getItem(SESSION_ROOM_ID);
  return {
    roomId: storedRoomId ? normalizeRoomId(storedRoomId) : null,
    username: sessionStorage.getItem(SESSION_USERNAME)
  };
};

// Connect function to explicitly control when the socket connects
export const connect = (): void => {
  if (!socket.connected) {
    socket.connect();
  }
};

export const isConnected = (): boolean => socket.connected;

const subscribe = (event: string, callback: (...args: any[]) => void): Unsubscribe => {
  socket.on(event, callback);
  return () => {
    socket.off(event, callback);
  };
};

// Event listeners
export const onConnect = (callback: () => void): Unsubscribe => {
  const unsubscribe = subscribe('connect', callback);
  connect();
  return unsubscribe;
};

export const onDisconnect = (callback: (reason: string) => void): Unsubscribe =>
  subscribe('disconnect', callback);

// Reconnection events live on the Manager (`socket.io`) in Socket.IO v3+, not on the socket.
export const onReconnect = (callback: () => void): Unsubscribe => {
  socket.io.on('reconnect', callback);
  return () => {
    socket.io.off('reconnect', callback);
  };
};

export const onReconnectAttempt = (callback: (attempt: number) => void): Unsubscribe => {
  socket.io.on('reconnect_attempt', callback);
  return () => {
    socket.io.off('reconnect_attempt', callback);
  };
};

export const onReconnectFailed = (callback: () => void): Unsubscribe => {
  socket.io.on('reconnect_failed', callback);
  return () => {
    socket.io.off('reconnect_failed', callback);
  };
};

export const onPlayerJoined = (callback: (data: PlayersUpdatePayload) => void): Unsubscribe =>
  subscribe('playerJoined', callback);

export const onPlayerLeft = (callback: (data: PlayersUpdatePayload) => void): Unsubscribe =>
  subscribe('playerLeft', callback);

export const onPlayerDisconnected = (callback: (data: PlayersUpdatePayload) => void): Unsubscribe =>
  subscribe('playerDisconnected', callback);

/** Fired on the *old* connection when the same player joins from another tab or device. */
export const onSessionReplaced = (callback: (data: { roomId: string }) => void): Unsubscribe =>
  subscribe('sessionReplaced', callback);

export const onPlayerSelected = (callback: (data: { playerId: string, username: string }) => void): Unsubscribe =>
  subscribe('playerSelected', callback);

export const onSongSelectionStarted = (callback: (data: { theme?: string }) => void): Unsubscribe =>
  subscribe('songSelectionStarted', callback);

export const onSongsPreloading = (callback: (data: { songs: Song[] }) => void): Unsubscribe =>
  subscribe('songsPreloading', callback);

export const onPreloadProgress = (callback: (data: { preloaded: number, total: number }) => void): Unsubscribe =>
  subscribe('preloadProgress', callback);

export const onGameStarted = (callback: (data: { songs: Song[] }) => void): Unsubscribe =>
  subscribe('gameStarted', callback);

export const onRoundStarted = (callback: (data: {
  round: number,
  totalRounds: number,
  songUrl: string,
  songTitle: string,
  roundStartTime: number,
  songSelector: { id: string, username: string } | null
}) => void): Unsubscribe => subscribe('roundStarted', callback);

export const onRoundEnded = (callback: (data: { round: number, song: Song }) => void): Unsubscribe =>
  subscribe('roundEnded', callback);

export const onGameEnded = (callback: (data: { scores: Player[] }) => void): Unsubscribe =>
  subscribe('gameEnded', callback);

export const onNewGuess = (callback: (data: any) => void): Unsubscribe =>
  subscribe('newGuess', callback);

export const onCorrectGuess = (callback: (data: any) => void): Unsubscribe =>
  subscribe('correctGuess', callback);

export const onCloseGuess = (callback: (data: any) => void): Unsubscribe =>
  subscribe('closeGuess', callback);

export const onRoomState = (callback: (data: RoomStatePayload) => void): Unsubscribe =>
  subscribe('roomState', (data: RoomStatePayload) => {
    if (data?.selfId) {
      adoptPlayerId(data.selfId);
    }
    callback(data);
  });

export const onRoomNotFound = (callback: () => void): Unsubscribe =>
  subscribe('roomNotFound', callback);

/** Human-readable explanation for a `joinRejected` reason. */
export const describeJoinRejection = (reason: string): string => {
  switch (reason) {
    case 'Game already in progress':
      return 'This game is already in progress. If you were playing, rejoin with the same username you used before.';
    case 'Username already taken':
      return 'That username is already taken in this room. Please choose another one.';
    default:
      return `Unable to join room: ${reason}`;
  }
};

export const onJoinRejected = (callback: (data: { reason: string }) => void): Unsubscribe =>
  subscribe('joinRejected', callback);

// Audio streaming event handlers
export const onAudioStreamStarting = (callback: (data: { videoId: string }) => void): Unsubscribe =>
  subscribe('audioStreamStarting', callback);

export const onAudioReady = (callback: (data: { videoId: string, audioUrl: string }) => void): Unsubscribe =>
  subscribe('audioReady', callback);

export const onAudioStreamError = (callback: (data: { videoId: string, error: string }) => void): Unsubscribe =>
  subscribe('audioStreamError', callback);

export const onSongDownloadStatus = (callback: (data: SongDownloadStatusPayload) => void): Unsubscribe =>
  subscribe('songDownloadStatus', callback);

export const onSongSelectionFailed = (callback: (data: { message: string, code?: string, details?: string }) => void): Unsubscribe =>
  subscribe('songSelectionFailed', callback);

export const onError = (callback: (data: { message: string, details?: string }) => void): Unsubscribe =>
  subscribe('error', callback);

export const onSongSelectorRewarded = (callback: (data: { playerId: string, username: string, score: number, totalScore: number }) => void): Unsubscribe =>
  subscribe('songSelectorRewarded', callback);

export const onThemeVoteSubmitted = (callback: (data: { playerId: string, username: string, currentVotes: number, requiredVotes: number }) => void): Unsubscribe =>
  subscribe('themeVoteSubmitted', callback);

export const onThemeVotePassed = (callback: (data: { playerId: string, username: string, pointsReverted: number }) => void): Unsubscribe =>
  subscribe('themeVotePassed', callback);

// Emitters with error handling
export const createRoom = (roomId: string, username: string) => {
  const normalizedRoomId = normalizeRoomId(roomId);
  if (!socket.connected) {
    console.warn('Socket not connected when attempting to create room. Connecting now.');
    socket.connect();
  }
  storeSessionInfo(normalizedRoomId, username);
  socket.emit('createRoom', { roomId: normalizedRoomId, username, playerId: getPlayerId() });
};

export const joinRoom = (roomId: string, username: string) => {
  const normalizedRoomId = normalizeRoomId(roomId);
  if (!socket.connected) {
    console.warn('Socket not connected when attempting to join room. Connecting now.');
    socket.connect();
  }
  // Store session info for potential reconnection
  storeSessionInfo(normalizedRoomId, username);
  socket.emit('joinRoom', { roomId: normalizedRoomId, username, playerId: getPlayerId() });
};

export const startGame = (roomId: string, theme?: string) => {
  if (!socket.connected) {
    console.warn('Socket not connected when attempting to start game.');
    return;
  }
  socket.emit('startGame', { roomId: normalizeRoomId(roomId), theme });
};

export const devAddBots = (roomId: string, count?: number, baseName?: string) => {
  if (process.env.NODE_ENV !== 'development') return;
  if (!socket.connected) {
    console.warn('Socket not connected when attempting to add dev bots.');
    return;
  }
  socket.emit('devAddBots', { roomId: normalizeRoomId(roomId), count, baseName });
};

export const selectSong = (roomId: string, song: Song): Promise<SelectSongAck> => {
  if (!socket.connected) {
    console.warn('Socket not connected when attempting to select song.');
    return Promise.resolve({ ok: false, error: 'Socket not connected' });
  }
  return new Promise<SelectSongAck>((resolve) => {
    socket.emit('selectSong', { roomId: normalizeRoomId(roomId), song }, (res: SelectSongAck) => {
      resolve(res);
    });
  });
};

export const submitGuess = (roomId: string, guess: string) => {
  if (!socket.connected) {
    console.warn('Socket not connected when attempting to submit guess.');
    return;
  }
  socket.emit('submitGuess', { roomId: normalizeRoomId(roomId), guess });
};

export const voteInvalidTheme = (roomId: string) => {
  if (!socket.connected) {
    console.warn('Socket not connected when attempting to vote invalid theme.');
    return;
  }
  socket.emit('voteInvalidTheme', { roomId: normalizeRoomId(roomId) });
};

export const requestAudioStream = (roomId: string, videoId: string) => {
  if (!socket.connected) {
    console.warn('Socket not connected when requesting audio stream.');
    return;
  }
  socket.emit('requestAudioStream', { roomId: normalizeRoomId(roomId), videoId });
};

export const getRoom = (roomId: string) => {
  const normalizedRoomId = normalizeRoomId(roomId);
  if (!socket.connected) {
    console.warn('Socket not connected when requesting room state.');
    socket.connect();
    socket.once('connect', () => {
      socket.emit('getRoom', { roomId: normalizedRoomId });
    });
    return;
  }
  socket.emit('getRoom', { roomId: normalizedRoomId });
};

export const disconnect = (): void => {
  socket.disconnect();
};

export default socket;
